import React, { useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './BrandOnboardingNamePanel.scss';

import { updateBrand } from '../../../Actions/BrandActions';
import { getBrand } from '../../../Helpers/user_helpers';
import { isValidDomain } from '../../../Helpers/formatting';
import { isEmpty } from 'lodash';

import Tooltip from '../../General/Tooltip';
import BrandOnboardingProgressBar from '../BrandOnboardingProgressBar';

const BrandOnboardingNamePanel = props => {
  const { user } = props;

  const updateField = (prev, idx, patch) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
  const [fields, setFields] = useState([
    {
      label: "Your brand's name",
      sublabel: 'This will be the visible name all creators see.',
      name: 'name',
      placeholder: 'Brand Name*',
      value: getBrand(user)?.name || '',
      isFieldValid: value => value?.length > 1,
      isValid: true,
      errorMessage: 'Please enter a valid email'
    },
    {
      label: 'Brand URL',
      sublabel: 'List your primary domain.',
      name: 'domain',
      placeholder: 'domain.com*',
      value: getBrand(user)?.domain || '',
      isFieldValid: value => isValidDomain(value),
      isValid: true,
      errorMessage: 'Please enter a valid domain. Do not include "www" or "https://".'
    }
  ]);

  const validateFields = () => {
    let firstErrorMessage = null;
    props.setIsLoading(true);

    const newFields = fields.map(field => {
      let isValidField = field.isFieldValid(field.value, field);
      if (!isValidField) firstErrorMessage = firstErrorMessage || field.errorMessage;
      return { ...field, isValid: isValidField };
    });

    setFields(newFields);
    props.setIsLoading(false);
    return firstErrorMessage;
  };

  const onNextClick = async () => {
    if (props.isLoading) return;
    const errorMessage = validateFields();
    if (errorMessage) return window.ALERT.error(errorMessage);

    const [name, domain] = fields.map(f => f.value.trim());
    const updates = {};
    if (name !== getBrand(user)?.name) updates.name = name;
    if (domain !== getBrand(user)?.domain) updates.domain = domain;

    if (!isEmpty(updates)) {
      props.setIsLoading(true);
      const brandUpdateResponse = await props.updateBrand(getBrand(user), updates);

      if (brandUpdateResponse.error) {
        props.setIsLoading(false);
        window.ALERT.error('An error occurred while updating your brand. Please try again.');
      } else {
        props.goToNextPanel();
      }
    } else {
      props.goToNextPanel();
    }
  };

  return (
    <div className='brand-onboarding-description-panel-outer'>
      <div className='brand-onboarding-description-panel-inner'>
        <div className='brand-onboarding-split-container-outer'>
          <div className='brand-onboarding-split-container-inner'>
            <div className='brand-onboarding-graphic-container-outer'>
              <div className='brand-onboarding-graphic-container-inner'>
                <div className='brand-onboarding-graphic-container-stat'>
                  <div className='brand-onboarding-graphic-container-stat-label'>Our Numbers</div>
                  <div className='brand-onboarding-graphic-container-stat-number'>{props.featuredStat.number}+</div>
                  <div className='brand-onboarding-graphic-container-stat-description'>{props.featuredStat.description}</div>
                </div>

                <BrandOnboardingProgressBar
                  goToPanel={props.goToPanel}
                  sections={props.sections}
                  activeSectionName={props.activeSectionName}
                  activePanelName={props.activePanelName}
                />
              </div>
              <div className='brand-onboarding-graphic-background'>
                <img className='brand-onboarding-graphic-background-image' src={props.backgroundImage} alt='name panel graphic' />
                <div className='brand-onboarding-graphic-background-overlay'></div>
              </div>
            </div>

            <div className='brand-onboarding-input-container-outer'>
              <div className='brand-onboarding-input-container-number-marker'>{props.panelNumber}</div>

              <div className={cn('brand-onboarding-input-container-inner', { isLoading: props.isLoading })}>
                <div className='brand-onboarding-title-text'>Brand Details</div>
                <div className='brand-onboarding-subtitle-text'>Please provide us with the following information and assets.</div>

                {fields.map((field, index) => (
                  <div key={index} className='brand-onboarding-input-container'>
                    <div className='brand-onboarding-input-label'>{field.label}</div>
                    <div className='brand-onboarding-input-sublabel'>{field.sublabel}</div>

                    <Tooltip message={field.isValid ? '' : field.errorMessage}>
                      {
                        <input
                          className={cn('brand-onboarding-input', { invalid: !field.isValid, filled: !!field.value })}
                          type='text'
                          placeholder={field.placeholder}
                          id={field.name}
                          value={field.value}
                          onChange={e => {
                            const { value } = e.target;
                            setFields(prev => updateField(prev, index, { value }));
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              onNextClick();
                            }

                            if (e.key === 'Escape') {
                              e.preventDefault();
                              const newFields = [...fields];
                              newFields[index].value = '';
                              setFields(newFields);
                            }
                          }}
                        />
                      }
                    </Tooltip>

                    {!!field.supportText && (
                      <div className={cn('creator-application-input-support-text', { alignRight: !field.inputValueAlignRight })}>
                        {field.supportText}
                      </div>
                    )}
                  </div>
                ))}

                <div className='brand-onboarding-pagination-buttons'>
                  <button className='brand-onboarding-button stretch' onClick={() => props.goToPreviousPanel()}>
                    Back
                  </button>
                  <button className='brand-onboarding-button stretch primary' onClick={onNextClick}>
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BrandOnboardingNamePanel.propTypes = {
  user: PropTypes.object.isRequired,

  goToPreviousPanel: PropTypes.func.isRequired,
  goToNextPanel: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  user: state.user
});

export default connect(mapStateToProps, {
  updateBrand
})(withRouter(BrandOnboardingNamePanel));
